import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Globe, Facebook, Instagram, Youtube, Linkedin, Music2 } from "lucide-react";
import { LOGO_CREST } from "@/lib/assets";

const SOCIALS = [
  { icon: Facebook, label: "Facebook", url: "#" },
  { icon: Instagram, label: "Instagram", url: "#" },
  { icon: Youtube, label: "YouTube", url: "https://www.youtube.com/@BLAISENANA-r3f" },
  { icon: Linkedin, label: "LinkedIn", url: "#" },
  { icon: Music2, label: "TikTok", url: "#" },
];

const COLS = [
  { title: "Explore", links: [["About", "/about"], ["Executive Profile", "/profile"], ["Ministry", "/ministry"], ["AI Academy", "/academy"], ["Consulting", "/consulting"], ["Business", "/business"]] },
  { title: "Encounter", links: [["Daily Devotional", "/devotional"], ["Prayer Wall", "/prayer"], ["Stories of Faith", "/stories"], ["Kingdom Studio", "/studio"]] },
  { title: "Engage", links: [["Store", "/store"], ["Watch Messages", "/watch"], ["Events", "/events"], ["Music", "/music"], ["Books", "/books"], ["Resources", "/resources"], ["Contact", "/contact"]] },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-gold-500/20 bg-ink" data-testid="footer">
      {/* Apostle's Signature Scripture — Romans 12:1-2 */}
      <div className="border-b border-gold-500/15 bg-gradient-to-b from-ink to-ink-paper/50" data-testid="apostle-signature">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-14 md:py-16 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold-500 mb-4">The Apostle's Foundation Scripture</p>
          <p className="font-accent italic text-2xl md:text-3xl lg:text-4xl text-white/90 leading-snug max-w-4xl mx-auto">
            "I beseech you therefore, brethren, by the mercies of God, that ye present your bodies a living sacrifice, holy, acceptable unto God, which is your reasonable service. And be not conformed to this world: but be ye transformed by the renewing of your mind…"
          </p>
          <p className="mt-5 text-sm text-gold-400 tracking-[0.15em]">— ROMANS 12:1–2</p>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-4">
              <img src={LOGO_CREST} alt="Apostle Blaise Judah crest" className="h-16 w-16 object-contain [mix-blend-mode:screen]" />
              <div className="leading-tight">
                <p className="font-serif text-xl text-white tracking-[0.06em]">APOSTLE BLAISE JUDAH</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold-500 mt-1.5">House of Blessing Ministries</p>
              </div>
            </div>
            <p className="mt-6 max-w-xs text-sm text-white/60 leading-relaxed">
              Equipping Kingdom leaders to transform nations through faith, leadership, business & innovation.
            </p>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold-500 mb-6">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-white/70 hover:text-gold-400 transition-colors duration-300">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold-500 mb-6">Connect</p>
            <ul className="space-y-4 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-gold-500 mt-0.5 shrink-0" />
                <span>+237 699 451 241<br />+237 653 468 440<br />+237 672 358 930</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-gold-500 mt-0.5 shrink-0" />
                <a href="mailto:info@apostleblaisejudah.com" className="hover:text-gold-400 transition-colors break-all">info@apostleblaisejudah.com</a>
              </li>
              <li className="flex items-start gap-3">
                <Globe size={16} className="text-gold-500 mt-0.5 shrink-0" />
                <span>www.apostleblaisejudah.com</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-gold-500 mt-0.5 shrink-0" />
                <span>P.O. Box 1, Tiko – Cameroon</span>
              </li>
            </ul>
            <div className="mt-7 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target={s.url.startsWith("http") ? "_blank" : undefined}
                  rel={s.url.startsWith("http") ? "noopener noreferrer" : undefined}
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="flex h-9 w-9 items-center justify-center border border-gold-500/30 text-gold-400 hover:bg-gold-500 hover:text-ink transition-colors duration-300"
                >
                  <s.icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-accent italic text-gold-300 text-lg">"Building Kingdom Leaders for Global Impact."</p>
          <p className="text-xs text-white/40">© {new Date().getFullYear()} House of Blessing Ministries. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
